import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Logs an activity to Firestore.
 * This function is designed to fail silently and not interrupt the user workflow.
 *
 * @param {object} logDetails - The details of the log.
 * @param {string} logDetails.billwaveCustomerId - The customer ID for the company.
 * @param {string} logDetails.action - The action being performed (e.g., 'invoice_csv_upload').
 * @param {'success' | 'failed'} logDetails.result - The result of the action.
 * @param {object} [logDetails.target] - The object being acted upon.
 * @param {string} [logDetails.target.type] - The type of the target (e.g., 'invoice_csv').
 * @param {string} [logDetails.target.id] - The ID of the target.
 * @param {string} [logDetails.target.name] - The name/description of the target.
 * @param {object} logDetails.actor - The user who performed the action.
 * @param {string} logDetails.actor.uid - The user's Firebase UID.
 * @param {string} [logDetails.actor.name] - The user's name.
 * @param {string} logDetails.actor.email - The user's email.
 * @param {object} [logDetails.details] - Additional metadata for the log.
 */
export const logActivity = async (logDetails) => {
  const { billwaveCustomerId, action, result, target, actor, details } = logDetails;

  try {
    // Ensure essential data is present for security rules and filtering
    if (!billwaveCustomerId || !action || !result || !actor || !actor.uid || !actor.email) {
      console.warn("logActivity: Missing required parameters for logging.", logDetails);
      return;
    }

    const db = getFirestore();

    const logData = {
      billwaveCustomerId,
      action,
      result,
      target_type: target?.type || null,
      target_id: target?.id || null,
      target_name: target?.name || null,
      actor_uid: actor.uid,
      actor_name: actor.name || null,
      actor_email: actor.email,
      details: details || {},
      created_at: serverTimestamp(),
    };

    await addDoc(collection(db, 'activity_logs'), logData);

  } catch (error) {
    // Log failures should not block or alert the user.
    // We log it to the console for developers to see.
    console.error("CRITICAL: Failed to write to activity log.", {
      description: "This is a critical failure in the logging mechanism itself.",
      errorDetails: error,
      logDataAttempted: logDetails
    });
  }
};
